import React, { useState } from "react";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import "./Support.css";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const CATEGORIES = [
  "Booking Issues",
  "Payment Problems",
  "Safety Concerns",
  "Technical Issues",
  "Account Help",
  "Feedback",
  "Other",
];
const MAX_MESSAGE_LENGTH = 1000;

export default function Support() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const messageValid = message.trim().length >= 5 && message.trim().length <= MAX_MESSAGE_LENGTH;
  const canSubmit = emailValid && category && messageValid && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (success) return; // already sent
    setError("");
    setSuccess("");
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          category,
          message: message.trim(),
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Could not send your message.");
      setSuccess("Your support message has been sent successfully, we'll contact you as soon as possible.");
      setEmail("");
      setName("");
      setCategory("");
      setMessage("");
    } catch (err) {
      setError(err.message || "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-container">
      <div className="support-card">
        <button className="pv-back" onClick={() => navigate("/dashboard")} aria-label="Back to dashboard">
          <ArrowBackIos fontSize="small" />
          <span>Back</span>
        </button>
        <h1 className="support-title">Contact Support</h1>
        <p className="support-subtitle">Send us a note and we’ll respond as soon as we can.</p>
        <form className="support-form" onSubmit={onSubmit}>
          <label className="support-label">
            Your Email
            <input
              type="email"
              className="support-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
            />
          </label>

          <label className="support-label">
            Your Name (optional)
            <input
              type="text"
              className="support-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
            />
          </label>

          <label className="support-label">
            Category
            <select
              className="support-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="support-label">
            Message
            <div className="support-textarea-wrap">
              <textarea
                className="support-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="How can we help?"
                maxLength={MAX_MESSAGE_LENGTH}
                required
              />
              <div className="support-charcount">{message.trim().length}/{MAX_MESSAGE_LENGTH}</div>
            </div>
          </label>

          {error && <div className="support-error">{error}</div>}

          <button
            type="submit"
            className="support-btn"
            disabled={!canSubmit || !!success}
          >
            {success ? "Sent" : submitting ? "Sending…" : "Send Message"}
          </button>
          {success && (
            <div className="support-success support-success-inline">
              {success}
              <div className="support-return-link-wrap">
                <a className="support-return-link" href="/dashboard">Go back to dashboard</a>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
